import { defineStore, acceptHMRUpdate } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
export const useSessionStore = defineStore('SessionStore', () => {
  if (!useAppRunningStore().isRunning) return;
  if (window.location.pathname === '/logout') return;

  const appEventSession = useAppEventSession();

  const context = useContextStore();

  const focused = ref(window.document.hasFocus());

  const stopListeningBlur = useEventListener(window, 'blur', () => {
    focused.value = false;
  });

  const stopListeningFocus = useEventListener(window, 'focus', () => {
    focused.value = true;
  });

  const id = ref<string>(uuidv4());

  const started = ref<boolean>(false);

  const user = useUserStore();

  const profile = useProfileStore();

  let timeoutTimer;

  const DELAY_BEFORE_END_OF_SESSION = 10000; // 10 secondes d'attente avant de fermer la session

  const stop = (immediate = false) => {
    const _stop = () => {
      if (!started.value) return;
      started.value = false;
      if (context.isLoggedIn && profile.data && user.data && !started.value) {
        appEventSession.stop();
      }
    };
    clearTimeout(timeoutTimer);
    if (immediate) return _stop();
    timeoutTimer = setTimeout(() => {
      _stop();
    }, DELAY_BEFORE_END_OF_SESSION);
  };

  const start = async () => {
    clearTimeout(timeoutTimer);
    if (context.isPdfViewer()) return;
    if (context.isLoggedIn && profile.data && user.data && !started.value) {
      globalThis.$logger.info('useSessionStore :: start');
      id.value = uuidv4();
      started.value = true;
      appEventSession.start();
    }
  };

  const quit = () => {
    stop(true);

    stopListeningBlur();
    stopListeningFocus();
  };

  window.addEventListener('beforeunload', (e) => {
    clearTimeout(timeoutTimer);
    stop(true);
  });

  watchEffect(
    () => {
      globalThis.$logger.info('useSessionStore :: watch effect', focused.value);
      if (!focused.value) {
        stop();
      } else {
        start();
      }
    },
    { flush: 'post' }
  );

  watch(
    () => profile.levelOfEducationId,
    async (to, from) => {
      if (from && !to) {
        stop();
      } else if (to && !from) {
        clearTimeout(timeoutTimer);
        start();
      }
    },
    { flush: 'post' }
  );

  return {
    stop,
    quit,
    started,
    id,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSessionStore, import.meta.hot));
}
